import { Component, ElementRef, ViewChild, AfterViewChecked } from '@angular/core';
import { SessionService } from '@cemex-core/angular-services-v8';
import { ILegalEntity } from '@cemex-core/types-v2/dist/index.interface';
import { getLanguage } from '@cemex/cmx-client-layout';
import { Store } from '@ngrx/store';
import { State } from 'app/cmx-client-layout/typings';

declare global {
  // tslint:disable-next-line:interface-name
  interface Window {
    chatWidget: any;
  }
}

export interface IAuth {
  access_token?: string | null;
  jwt?: string | null;
  customer_id?: number | null;
  customer_code?: string | null;
  country_code?: string | null;
  user_id?: string | null;
  user_timezone?: string | null;
}

@Component({
  selector: 'cmx-wizeline-chatbot',
  styleUrls: ['wizeline-chatbot.component.scss'],
  templateUrl: 'wizeline-chatbot.component.html',
  standalone: true,
})
export class WizelineChatbotComponent implements AfterViewChecked {
  @ViewChild('botWindow') public botWindowElement: ElementRef;
  private globalCustomerCode: string | any;
  private widgetHasInit: boolean;
  private language: string | null = null;
  private get customerCode(): string | null {
    const customer = this.sessionService.currentLegalEntityValue;
    return customer ? customer.legalEntityTypeCode : null;
  }

  constructor(
    private sessionService: SessionService,
    private store: Store<State>,
  ) {
    this.widgetHasInit = false;
    // subscribe to required services
    this.sessionService.currentLegalEntity.subscribe((legalEntity: ILegalEntity) => {
      if (legalEntity !== null) {
        this.onDetectChangesSessionStore();
        if (!this.globalCustomerCode) {
          const newChatbotAuth: any = this.getChatbotAuth();
          this.setCredentials(newChatbotAuth);
          this.globalCustomerCode = this.sessionService.currentLegalEntityValue.legalEntityTypeCode;
        }
      }
    });
    this.store.select(getLanguage).subscribe((language) => {
      this.onDetectChangesLanguage(language);
    });
  }

  public ngAfterViewChecked(): void {
    if (!this.widgetHasInit) {
      this.initChatbot();
    }
  }

  public initChatbot(): void {
    try {
      this.fireEvent('MountEvent', window);
      /* Change to actual department name
       * This may be the country code in local storage
       * For testing purposes its set to Support English
       */
      // handle account keys in CDN
      // window.chatWidget.setDepartment([GET COUNTRY HERE]);
      this.widgetHasInit = true;
    } catch (err) {
      // log errors if necesary
      // console.log('ERROR LOADING', err);
    }
  }

  public onDetectChangesSessionStore(): void {
    const changedCustomerCode: any = this.globalCustomerCode !== this.customerCode;
    const newChatbotAuth: any = this.getChatbotAuth();
    if (changedCustomerCode) {
      const justLoggedIn: any = !this.globalCustomerCode;
      if (!justLoggedIn) {
        this.updateLegalEntity(this.customerCode, newChatbotAuth);
      } else {
        this.setCredentials(newChatbotAuth);
        this.onDetectChangesLanguage(this.language || 'en_US');
      }
      this.globalCustomerCode = changedCustomerCode;
    }
  }

  public onDetectChangesLanguage(language: string): void {
    this.language = language;
    if (window.chatWidget) {
      const currentLanguage: string = language.substring(0, 2);
      if (currentLanguage === 'es') {
        window.chatWidget.setLang('es');
        window.chatWidget.setDepartment('Spanish');
      } else {
        window.chatWidget.setLang('en');
        window.chatWidget.setDepartment('English');
      }
    }
  }

  private getChatbotAuth(): object {
    const auth: IAuth = {
      access_token: undefined,
      country_code: undefined,
      customer_code: undefined,
      customer_id: undefined,
      jwt: undefined,
      user_id: undefined,
      user_timezone: undefined,
    };
    const userLegalEntity: any | null = sessionStorage.getItem('user_legal_entity');
    const userProfile: any | null = sessionStorage.getItem('user_profile');
    let legalEntityId: any;
    let userId: any;
    if (userLegalEntity) {
      legalEntityId = JSON.parse(userLegalEntity).legalEntityId;
    }
    if (userProfile) {
      userId = JSON.parse(userProfile).userId;
    }
    const timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (
      this.sessionService.isLoggedIn &&
      this.sessionService.userProfile &&
      this.sessionService.userProfile.customerId &&
      this.sessionService.country
    ) {
      auth.access_token = this.sessionService.access_token;
      auth.jwt = this.sessionService.jwt;
      if (userLegalEntity) {
        auth.customer_id = legalEntityId;
      } else {
        auth.customer_id = this.sessionService.userProfile.customerId;
      }
      auth.customer_code = this.customerCode;
      auth.country_code = this.sessionService.country;
      if (userId) {
        auth.user_id = userId;
      }
      auth.user_timezone = timezone;
    }
    return auth;
  }

  private setCredentials(auth: any): void {
    const newAuth: any = {
      data: auth,
      integration: 'cemex',
    };
    if (auth && typeof auth === 'object' && Object.keys(auth).length !== 0 && window.chatWidget) {
      if (!this.widgetHasInit) {
        this.initChatbot();
      }
      window.chatWidget.setCredentialsAuth(newAuth);
    }
  }

  private updateLegalEntity(customerCode: string | null, newChatbotAuth: any): void {
    if (window.chatWidget) {
      const userLegalEntity: any | null = sessionStorage.getItem('user_legal_entity');
      const legalEntityId: any = JSON.parse(userLegalEntity).legalEntityId;
      newChatbotAuth.customer_code = customerCode;
      newChatbotAuth.customer_id = legalEntityId;

      const newAuth: any = {
        data: newChatbotAuth,
        integration: 'cemex',
      };
      window.chatWidget.sendLegalEntityCode(newAuth);
    }
  }

  private fireEvent(name: string, target: any): void {
    target.dispatchEvent(new Event(name, { bubbles: true, cancelable: true }));
  }
}
